import React, { useMemo } from 'react';
import { STATUS_LABELS, STATUS_VARIANT } from '../utils/statusUtils'

function BookingDetailsModal({ booking, onClose }) {
  if (!booking) return null;

  const {
    productName,
    destination,
    stayStartDate,
    stayEndDate,
    durationNights,
    durationDays,
    adults,
    children,
    currency,
    totalPrice,
    finalTotalPrice,
    discountValue,
    status,
    selectedRoom = {},
    selectedFlights = {},
    quoteLines = [],
  } = booking;

  const segments = selectedFlights.segments || [];
  const luggagesSelection = selectedFlights.luggagesSelection || selectedFlights.luggages || null;

  const groupedSegments = useMemo(() => {
    const res = { outbound: [], inbound: [] };
    segments.forEach((s) => {
      if (s.direction === 'inbound') res.inbound.push(s);
      else res.outbound.push(s);
    });
    return res;
  }, [segments]);

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  const formatMoney = (v) => `${Number(v || 0).toFixed(2)} ${currency || 'EUR'}`;

  const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '';

               const BAGGAGE_LABELS = {
  CHECKED_BAGS: 'Bagage en soute',
  CABIN_BAG: 'Bagage cabine',
};

const formatPrice = (value, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
  }).format(value);

  return (
    <div className="modal-backdrop show" style={{ display: 'block' }}>
      <div className="modal d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Détail de la réservation</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"/>
            </div>

            <div className="modal-body">
              {/* Bloc résumé */}
              <div className="mb-3">
                <h6 className="fw-semibold mb-1">{productName}</h6>
                 <div className="small text-muted">
  {destination?.city}
  {' · '}
  Séjour du {formatDate(stayStartDate)} au {formatDate(stayEndDate)}
  {' · '}
                  {' · '}
                  {durationNights || durationDays} nuits · {adults} ad, {children} enf
                </div>
                <div className="mt-2">
                  Sous-total : {formatMoney(totalPrice)}{' '}
                  {discountValue ? (
                    <>
                      · Remise B2B : {discountValue}% · Total :{' '}
                      <strong>{formatMoney(finalTotalPrice)}</strong>
                    </>
                  ) : (
                    <>· Total : <strong>{formatMoney(finalTotalPrice)}</strong></>
                  )}
                  {' · '}
                  Statut :{' '}
<span
style={{color:'#ffffff !important'}}
  className={`badge bg-${
    STATUS_VARIANT[status] ?? 'warning'
  }`}
>
  {STATUS_LABELS[status] ?? 'En cours'}
</span>
                </div>
              </div>

              {/* Chambre & pension */}
              <hr />
              <h6>Hébergement</h6>
              {selectedRoom && Object.keys(selectedRoom).length > 0 ? (
                <>
                  <div>{selectedRoom.roomLabel || selectedRoom.category}</div>
                  <div className="small text-muted">
                    Pension : {selectedRoom.mealPlanLabel || selectedRoom.mealPlan || '—'}
                  </div>
                </>
              ) : (
                <div className="text-muted small">Aucune chambre enregistrée.</div>
              )}

              {/* Vols */}
              <hr />
              <h6>Vols</h6>
              {segments.length === 0 ? (
                <div className="text-muted small">
                  Aucun détail de vol enregistré pour cette réservation.
                </div>
              ) : (
                <>
                  {groupedSegments.outbound.length > 0 && (
                    <div className="mb-2">
                      <div className="fw-semibold mb-1">Aller</div>
                      <ul className="small mb-0">
                        {groupedSegments.outbound.map((s, i) => (
                          <li key={`out-${i}`}>
                            {s.from} → {s.to} · {s.carrier}{' '}
                            {s.flightNumber} · départ {formatDateTime(s.departure)} · arrivée{' '}
                            {formatDateTime(s.arrival)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {groupedSegments.inbound.length > 0 && (
                    <div>
                      <div className="fw-semibold mb-1">Retour</div>
                      <ul className="small mb-0">
                        {groupedSegments.inbound.map((s, i) => (
                          <li key={`in-${i}`}>
                            {s.from} → {s.to} · {s.carrier}{' '}
                            {s.flightNumber} · départ {formatDateTime(s.departure)} · arrivée{' '}
                            {formatDateTime(s.arrival)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* Bagages */}
              <hr />


<h6>Bagages</h6>

{luggagesSelection ? (
  <div className="bg-light p-3 rounded small">
    <ul className="mb-2">
      {Object.entries(luggagesSelection.perPax || {}).map(
        ([paxId, baggageCode]) => (
          <li key={paxId}>
            <strong>Passager {paxId}</strong> :{' '}
            {BAGGAGE_LABELS[baggageCode] ?? baggageCode}
          </li>
        ),
      )}
    </ul>

    <div className="d-flex justify-content-between border-top pt-2 fw-semibold">
      <span>Total bagages</span>
      <span>
        {formatPrice(
          luggagesSelection.total,
          luggagesSelection.currency,
        )}
      </span>
    </div>
  </div>
) : (
  <div className="text-muted small">
    Aucun supplément bagage sélectionné.
  </div>
)}
              {/* Détail devis */}
              <hr />
              <h6>Détail du devis</h6>
              {quoteLines && quoteLines.length ? (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Libellé</th>
                      <th className="text-end">Qté</th>
                      <th className="text-end">PU</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteLines.map((l, i) => (
                      <tr key={i}>
                        <td>{l.label}</td>
                        <td className="text-end">{l.qty}</td>
                        <td className="text-end">{formatMoney(l.unitPrice)}</td>
                        <td className="text-end">{formatMoney(l.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-muted small">Pas de détail de devis enregistré.</div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingDetailsModal;
